import { useCallback, useEffect, useRef, useState } from "react";
import type { Notification } from "../types";

interface Props {
  /** Navega para o destino de uma notificação com link (ex.: abrir a conversa atribuída). */
  onOpenLink?: (link: string) => void;
}

const POLL_MS = 30_000;

const TYPE_ICON: Record<Notification["type"], string> = {
  user_pending_approval: "👤",
  account_approved: "✅",
  invite_accepted: "🤝",
  budget_alert: "⚠️",
  session_assigned: "💬",
  scheduled_prompt_ran: "⏰",
  usage_anomaly: "📈",
  engine_failure: "🛑",
};

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "agora";
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export function NotificationBell({ onOpenLink }: Props) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    fetch("/api/notifications")
      .then((r) => (r.ok ? r.json() : { items: [], unreadCount: 0 }))
      .then((j: { items: Notification[]; unreadCount: number }) => {
        setItems(j.items ?? []);
        setUnreadCount(j.unreadCount ?? 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openItem = async (n: Notification) => {
    if (!n.readAt) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await fetch(`/api/notifications/${encodeURIComponent(n.id)}/read`, { method: "POST" });
      } catch {
        /* ignore */
      }
    }
    if (n.link && onOpenLink) {
      onOpenLink(n.link);
      setOpen(false);
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, readAt: x.readAt ?? new Date().toISOString() })));
    setUnreadCount(0);
    try {
      await fetch("/api/notifications/read-all", { method: "POST" });
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="notif-bell-wrap" ref={ref}>
      <button
        type="button"
        className="notif-bell-btn"
        onClick={() => setOpen((v) => !v)}
        title="Notificações"
        aria-label="Notificações"
        aria-expanded={open}
      >
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M4 6.5a4 4 0 0 1 8 0c0 3 1 4 1 4H3s1-1 1-4Z" />
          <path d="M6.5 12.5a1.5 1.5 0 0 0 3 0" />
        </svg>
        {unreadCount > 0 && <span className="notif-bell-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
      </button>
      {open && (
        <div className="notif-popover">
          <div className="notif-popover-header">
            <span>Notificações</span>
            {unreadCount > 0 && (
              <button type="button" className="notif-mark-all" onClick={() => void markAllRead()}>
                Marcar tudo como lido
              </button>
            )}
          </div>
          <div className="notif-list">
            {items.length === 0 ? (
              <p className="notif-empty">Sem notificações por enquanto.</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`notif-item ${n.readAt ? "" : "unread"}`}
                  onClick={() => void openItem(n)}
                >
                  <span className="notif-item-icon" aria-hidden>
                    {TYPE_ICON[n.type] ?? "🔔"}
                  </span>
                  <span className="notif-item-text">
                    <span className="notif-item-title">{n.title}</span>
                    {n.body && <span className="notif-item-body">{n.body}</span>}
                  </span>
                  <span className="notif-item-time">{timeAgo(n.createdAt)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
